import React, { useState } from "react";
import { Upload, Button, message, Card, Typography, Space, Alert, Input } from "antd";
import { InboxOutlined, FilePdfOutlined } from "@ant-design/icons";
import { uploadFormatoAceptacion } from "../../../../services/financiero/plandecompras/getFormatoAceptacionPlanCompraPattiService";
import { useSelector } from "react-redux";

const { Text } = Typography;
const { Dragger } = Upload;

const UploadDocumentoFirmadoFormatoAceptacion = ({
  id_registro,
  onClose,
  nombreArchivo,
  tipo_formato,
  codigo_interno,
  updateData
}) => {
  const [file, setFile] = useState(null);
  const [cedulaFile, setCedulaFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCodigoInput, setShowCodigoInput] = useState(false);
  const [codigoIngresado, setCodigoIngresado] = useState("");
  const [codigoError, setCodigoError] = useState("");
  const usernameFromState = useSelector((state) => state.auth.user);

  const handleFileChange = (info, isCedula = false) => {
    const selectedFile = info.fileList?.[0]?.originFileObj;
    if (!selectedFile) return;

    if (selectedFile.type !== "application/pdf") {
      message.error("Solo se permiten archivos en formato PDF.");
      return;
    }

    if (isCedula) {
      setCedulaFile(selectedFile);
    } else {
      setFile(selectedFile);
    }
    
    // Resetear el estado del código cuando se selecciona un nuevo archivo
    setShowCodigoInput(false);
    setCodigoIngresado("");
    setCodigoError("");
  };

  const handleUpload = async () => {
    // Validar archivos requeridos
    if (!file || (tipo_formato === 2 && !cedulaFile)) {
      message.warning("Por favor selecciona todos los archivos requeridos.");
      return;
    }

    // Si se muestra el input de código, validar que no esté vacío
    if (showCodigoInput && !codigoIngresado.trim()) {
      message.warning("Por favor ingresa el código único.");
      return;
    }

    setLoading(true);

    try {
      const result = await uploadFormatoAceptacion(
        file,
        tipo_formato === 2 ? cedulaFile : null,
        id_registro,
        tipo_formato,
        nombreArchivo,
        tipo_formato === 2 ? `cedula_${id_registro}.pdf` : null,
        tipo_formato,
        codigo_interno,
        usernameFromState?.username,
        showCodigoInput ? codigoIngresado.trim() : undefined
      );

      if (result.success) {
        message.success("📄 Documentos subidos correctamente.");
        
        // Resetear todos los estados
        setFile(null);
        setCedulaFile(null);
        setShowCodigoInput(false);
        setCodigoIngresado("");
        setCodigoError("");
        
        updateData();
        onClose();
      } else {
        
        const errorCode = result.errorCode || result.error_code || result.code;
        const errorMessage = result.message || result.error || result.msg;
        
        if (errorCode === "NO_CODIGO_INTERNO") {
          message.error("❌ El PDF no contiene el campo 'Código Único'.");
        } else if (errorCode === "CODIGO_NO_COINCIDE") {
          // Activar el input manual y mostrar el error
          setShowCodigoInput(true);
          setCodigoError(
            `El código "${errorMessage}" no coincide con el esperado. Por favor, ingresa el código manualmente.`
          );
          message.warning("El código no coincide. Por favor ingresa el código manualmente.");
        } else {
          // Si no hay errorCode específico, revisar el mensaje
          if (errorMessage && (errorMessage.includes("no coincide") || errorMessage.includes("CODIGO_NO_COINCIDE"))) {
            setShowCodigoInput(true);
            setCodigoError(
              `El código no coincide con el esperado. Por favor, ingresa el código manualmente.`
            );
            message.warning("El código no coincide. Por favor ingresa el código manualmente.");
          } else {
            message.error(
              `❌ ${errorMessage || "Error al subir los documentos."}`
            );
          }
        }
      }
    } catch (error) {
      message.error("❌ Error de conexión al servidor.");
    } finally {
      setLoading(false);
    }
  };

  // Función para validar si el botón debe estar habilitado
  const isButtonDisabled = () => {
    // Si no hay archivo principal
    if (!file) return true;
    
    // Si es tipo 2 y no hay cédula
    if (tipo_formato === 2 && !cedulaFile) return true;
    
    // Si se muestra el input de código y está vacío
    if (showCodigoInput && !codigoIngresado.trim()) return true;
    
    return false;
  };

  return (
    <Card
      style={{
        maxWidth: 600,
        margin: "40px auto",
        borderRadius: 12,
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        padding: 24,
        backgroundColor: "#ffffff",
      }}
    >
      {/* Alerta informativa */}
      <Alert
        message="Importante"
        description="El archivo que cargues debe tener buena calidad de imagen. Asegúrate de que el código único ubicado en la parte inferior de la última página sea completamente legible."
        type="warning"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
        Arrastra y suelta el archivo firmado o haz clic para seleccionarlo. Solo
        se acepta formato PDF.
      </Text>

      {/* Documento firmado */}
      <Dragger
        beforeUpload={() => false}
        onChange={(info) => handleFileChange(info, false)}
        accept=".pdf"
        maxCount={1}
        showUploadList={false}
        style={{
          backgroundColor: "#fafafa",
          border: "1px dashed #d9d9d9",
          borderRadius: 8,
          padding: 24,
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined style={{ color: "#004080", fontSize: 32 }} />
        </p>
        <p className="ant-upload-text">Documento firmado (.pdf)</p>
      </Dragger>

      {file && (
        <Space
          style={{
            marginTop: 16,
            padding: 12,
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            background: "#f6ffed",
          }}
        >
          <FilePdfOutlined style={{ color: "#cf1322", fontSize: 20 }} />
          <Text strong>{file.name}</Text>
        </Space>
      )}

      {/* Cédula (solo si tipo_formato === 2) */}
      {tipo_formato === 2 && (
        <>
          <Text
            type="secondary"
            style={{ display: "block", margin: "24px 0 12px" }}
          >
            Ahora, sube la copia de la cédula firmada (PDF).
          </Text>

          <Dragger
            beforeUpload={() => false}
            onChange={(info) => handleFileChange(info, true)}
            accept=".pdf"
            maxCount={1}
            showUploadList={false}
            style={{
              backgroundColor: "#fafafa",
              border: "1px dashed #d9d9d9",
              borderRadius: 8,
              padding: 24,
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ color: "#004080", fontSize: 32 }} />
            </p>
            <p className="ant-upload-text">Cédula firmada (.pdf)</p>
          </Dragger>

          {cedulaFile && (
            <Space
              style={{
                marginTop: 16,
                padding: 12,
                border: "1px solid #f0f0f0",
                borderRadius: 8,
                background: "#fffbe6",
              }}
            >
              <FilePdfOutlined style={{ color: "#fa8c16", fontSize: 20 }} />
              <Text strong>{cedulaFile.name}</Text>
            </Space>
          )}
        </>
      )}

      {/* Campo para ingresar el código si es necesario */}
      {showCodigoInput && (
        <div style={{ marginTop: 24 }}>
          <Alert
            message="El código no coincide"
            description={codigoError}
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
          />
          <Text strong>Ingresa el código único manualmente:</Text>
          <Input
            value={codigoIngresado}
            onChange={(e) => {
              setCodigoIngresado(e.target.value);
              setCodigoError(""); // Limpia el error al escribir
            }}
            placeholder="Código único"
            style={{ marginTop: 8, width: "100%", maxWidth: 300 }}
            maxLength={50}
            onPressEnter={handleUpload} // Permitir envío con Enter
          />
        </div>
      )}

      <Button
        type="primary"
        block
        onClick={handleUpload}
        loading={loading}
        disabled={isButtonDisabled()}
        style={{ marginTop: 32 }}
      >
        {showCodigoInput ? "Reenviar con Código Manual" : "Subir al Servidor"}
      </Button>
    </Card>
  );
};

export default UploadDocumentoFirmadoFormatoAceptacion;